import type { VaultArtifactStore } from "../backend/vault-artifacts";

const CHUNK_BYTES = 64 * 1024;
const MAX_TRANSFER_BYTES = 128 * 1024 * 1024;
const SIGNAL_VERSION = 1;

type Signal = {
  v: 1;
  type: "offer" | "answer";
  sdp: RTCSessionDescriptionInit;
};

type TransferMeta = {
  type: "meta";
  name: string;
  size: number;
  sha256: string;
};

type ResumeMessage = {
  type: "resume";
  sha256: string;
  offset: number;
  alreadyPresent?: boolean;
};

type ResumeState = {
  offset: number;
  alreadyPresent: boolean;
};

type BundleEntry = Omit<TransferMeta, "type"> & { index: number };

export type PeerTransferRole = "idle" | "sender" | "receiver";

export class PeerArtifactTransfer {
  private connection?: RTCPeerConnection;
  private channel?: RTCDataChannel;
  private receiveChunks: ArrayBuffer[] = [];
  private receiveBytes = 0;
  private receiveMeta?: TransferMeta;
  private receiveAlreadyPresent = false;
  private receiveBundle?: { entries: BundleEntry[]; received: Set<number> };
  private receiveBundleIndex?: number;
  private readonly fileAckWaiters = new Map<number, { resolve: () => void; reject: (error: Error) => void; timer: number }>();
  private readonly resumeWaiters = new Map<string, { resolve: (state: ResumeState) => void; reject: (error: Error) => void; timer: number }>();
  private role: PeerTransferRole = "idle";

  constructor(
    private readonly store: VaultArtifactStore,
    private readonly onStatus: (message: string, error?: boolean) => void,
    private readonly onProgress: (completed: number, total: number) => void = () => {},
  ) {}

  get currentRole(): PeerTransferRole {
    return this.role;
  }

  async createOffer(stunUrl = ""): Promise<string> {
    this.reset();
    const connection = this.createConnection(stunUrl);
    this.role = "sender";
    this.channel = connection.createDataChannel("opaque-vault-artifact", { ordered: true });
    this.channel.binaryType = "arraybuffer";
    this.channel.onopen = () => this.onStatus("Sender channel open. Apply the receiver answer.");
    this.channel.onmessage = (message) => void this.handleReceiveMessage(message.data).catch((error) => this.onStatus(error instanceof Error ? error.message : String(error), true));
    this.channel.onerror = () => this.onStatus("Sender DataChannel error.", true);

    await connection.setLocalDescription(await connection.createOffer());
    await waitForIceGathering(connection);
    if (!connection.localDescription) throw new Error("Sender offer was not created.");
    this.onStatus("Offer created. Send it to the receiver.");
    return encodeSignal({ v: SIGNAL_VERSION, type: "offer", sdp: connection.localDescription });
  }

  async createAnswer(offerCode: string, stunUrl = ""): Promise<string> {
    this.reset();
    const offer = decodeSignal(offerCode);
    if (offer.type !== "offer") throw new Error("Expected a sender offer.");

    const connection = this.createConnection(stunUrl);
    this.role = "receiver";
    const channelReady = new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error("Receiver channel did not arrive.")), 20_000);
      connection.ondatachannel = (event) => {
        window.clearTimeout(timer);
        this.channel = event.channel;
        this.channel.binaryType = "arraybuffer";
        this.channel.onopen = () => resolve();
        this.channel.onmessage = (message) => void this.handleReceiveMessage(message.data).catch((error) => this.onStatus(error instanceof Error ? error.message : String(error), true));
        this.channel.onerror = () => reject(new Error("Receiver DataChannel error."));
      };
    });

    await connection.setRemoteDescription(offer.sdp);
    await connection.setLocalDescription(await connection.createAnswer());
    await waitForIceGathering(connection);
    if (!connection.localDescription) throw new Error("Receiver answer was not created.");
    this.onStatus("Answer created. Send it back to the sender.");
    void channelReady.catch((error) => this.onStatus(error.message, true));
    return encodeSignal({ v: SIGNAL_VERSION, type: "answer", sdp: connection.localDescription });
  }

  async applyAnswer(answerCode: string, artifactId: string): Promise<void> {
    if (!this.connection || this.role !== "sender" || !this.channel) {
      throw new Error("Create an offer before applying an answer.");
    }
    const answer = decodeSignal(answerCode);
    if (answer.type !== "answer") throw new Error("Expected a receiver answer.");
    await this.connection.setRemoteDescription(answer.sdp);
    await waitForDataChannelOpen(this.channel);
    await this.sendArtifact(artifactId);
  }

  async applyAnswerAndSendBundle(answerCode: string, artifactIds: string[]): Promise<void> {
    if (!this.connection || this.role !== "sender" || !this.channel) {
      throw new Error("Create an offer before applying an answer.");
    }
    if (artifactIds.length === 0) throw new Error("Select at least one artifact.");
    const answer = decodeSignal(answerCode);
    if (answer.type !== "answer") throw new Error("Expected a receiver answer.");
    await this.connection.setRemoteDescription(answer.sdp);
    await waitForDataChannelOpen(this.channel);
    await this.sendBundle(artifactIds);
  }

  dispose(): void {
    this.reset();
  }

  private createConnection(stunUrl: string): RTCPeerConnection {
    const iceServers = stunUrl.trim() ? [{ urls: stunUrl.trim() }] : [];
    this.connection = new RTCPeerConnection({ iceServers });
    this.connection.onconnectionstatechange = () => {
      if (this.connection?.connectionState === "failed") this.onStatus("Peer connection failed.", true);
    };
    return this.connection;
  }

  private async sendArtifact(artifactId: string): Promise<void> {
    const value = await this.store.getBlob(artifactId);
    if (!value) throw new Error("Selected opaque artifact no longer exists.");
    if (value.blob.size > MAX_TRANSFER_BYTES) throw new Error("Artifact exceeds the transfer limit.");
    if (!this.channel || this.channel.readyState !== "open") throw new Error("Peer channel is not open.");

    const meta: TransferMeta = {
      type: "meta",
      name: value.metadata.name,
      size: value.blob.size,
      sha256: value.metadata.sha256,
    };
    this.channel.send(JSON.stringify(meta));
    const resume = await this.waitForResume(value.metadata.sha256);
    if (!Number.isInteger(resume.offset) || resume.offset < 0 || resume.offset > value.blob.size) {
      throw new Error("Receiver returned an invalid resume offset.");
    }
    if (resume.alreadyPresent || resume.offset === value.blob.size) {
      this.onProgress(value.blob.size, value.blob.size);
      this.channel.send(JSON.stringify({ type: "complete" }));
      this.onStatus(`Encrypted artifact ${value.metadata.name} was already present on receiver.`);
      return;
    }
    const bytes = new Uint8Array(await value.blob.arrayBuffer());
    this.onProgress(resume.offset, bytes.byteLength);
    for (let offset = resume.offset; offset < bytes.byteLength; offset += CHUNK_BYTES) {
      await waitForBufferedAmount(this.channel);
      this.channel.send(bytes.slice(offset, Math.min(bytes.byteLength, offset + CHUNK_BYTES)));
      this.onProgress(Math.min(bytes.byteLength, offset + CHUNK_BYTES), bytes.byteLength);
    }
    this.channel.send(JSON.stringify({ type: "complete" }));
    this.onStatus(`Sent encrypted artifact ${value.metadata.name}.`);
  }

  private async sendBundle(artifactIds: string[]): Promise<void> {
    if (!this.channel || this.channel.readyState !== "open") throw new Error("Peer channel is not open.");
    const values = [];
    let totalBytes = 0;
    for (const id of artifactIds) {
      const value = await this.store.getBlob(id);
      if (!value) throw new Error("One selected opaque artifact no longer exists.");
      if (value.blob.size > MAX_TRANSFER_BYTES || totalBytes + value.blob.size > MAX_TRANSFER_BYTES) {
        throw new Error("Bundle exceeds the 128 MiB transfer limit.");
      }
      values.push(value);
      totalBytes += value.blob.size;
    }

    const entries: BundleEntry[] = values.map((value, index) => ({
      index,
      name: value.metadata.name,
      size: value.blob.size,
      sha256: value.metadata.sha256,
    }));
    this.channel.send(JSON.stringify({ type: "bundle-meta", entries }));
    let completed = 0;
    this.onProgress(0, totalBytes);
    for (const [index, value] of values.entries()) {
      const entry = entries[index];
      this.channel.send(JSON.stringify({ type: "bundle-file", ...entry }));
      const bytes = new Uint8Array(await value.blob.arrayBuffer());
      for (let offset = 0; offset < bytes.byteLength; offset += CHUNK_BYTES) {
        await waitForBufferedAmount(this.channel);
        const end = Math.min(bytes.byteLength, offset + CHUNK_BYTES);
        this.channel.send(bytes.slice(offset, end));
        completed += end - offset;
        this.onProgress(completed, totalBytes);
      }
      this.channel.send(JSON.stringify({ type: "file-complete", index }));
      await this.waitForFileAck(index);
    }
    this.channel.send(JSON.stringify({ type: "bundle-complete", count: entries.length }));
    this.onStatus(`Sent ${entries.length} encrypted artifacts.`);
  }

  private async handleReceiveMessage(data: unknown): Promise<void> {
    if (typeof data === "string") {
      const message = JSON.parse(data) as TransferMeta | ResumeMessage | { type: "complete" } | { type: "file-ack"; index: number; ok: boolean; error?: string } | { type: "bundle-meta"; entries: BundleEntry[] } | { type: "bundle-file"; index: number; name: string; size: number; sha256: string } | { type: "file-complete"; index: number } | { type: "bundle-complete"; count: number };
      if (message.type === "bundle-meta") {
        const entries = message.entries;
        const total = entries.reduce((sum, entry) => sum + Number(entry.size || 0), 0);
        if (!Array.isArray(entries) || entries.length === 0 || entries.length > 100 || total > MAX_TRANSFER_BYTES) {
          throw new Error("Incoming bundle exceeds transfer limits.");
        }
        this.receiveBundle = { entries, received: new Set() };
        this.onStatus(`Receiving ${entries.length} encrypted artifacts…`);
      } else if (message.type === "bundle-file") {
        if (!this.receiveBundle || !this.receiveBundle.entries.some((entry) => entry.index === message.index)) {
          throw new Error("Unexpected bundle file entry.");
        }
        this.receiveBundleIndex = message.index;
        this.receiveMeta = { type: "meta", name: message.name, size: message.size, sha256: message.sha256 };
        this.receiveAlreadyPresent = false;
        this.receiveChunks = [];
        this.receiveBytes = 0;
      } else if (message.type === "file-complete") {
        if (!this.receiveBundle || this.receiveBundleIndex !== message.index) throw new Error("Bundle file is incomplete.");
        try {
          await this.finishReceive();
          this.receiveBundle.received.add(message.index);
          this.channel?.send(JSON.stringify({ type: "file-ack", index: message.index, ok: true }));
          this.receiveMeta = undefined;
          this.receiveBundleIndex = undefined;
        } catch (error) {
          this.channel?.send(JSON.stringify({ type: "file-ack", index: message.index, ok: false, error: error instanceof Error ? error.message : String(error) }));
          throw error;
        }
      } else if (message.type === "bundle-complete") {
        if (!this.receiveBundle || this.receiveBundle.received.size !== this.receiveBundle.entries.length) {
          throw new Error("Bundle completed before every file was received.");
        }
        this.onStatus(`Received ${this.receiveBundle.entries.length} encrypted artifacts.`);
        this.receiveBundle = undefined;
      } else if (message.type === "file-ack") {
        const waiter = this.fileAckWaiters.get(message.index);
        if (waiter) {
          window.clearTimeout(waiter.timer);
          this.fileAckWaiters.delete(message.index);
          message.ok ? waiter.resolve() : waiter.reject(new Error(message.error || "Receiver rejected the file."));
        }
      } else if (message.type === "resume") {
        const waiter = this.resumeWaiters.get(message.sha256);
        if (waiter) {
          window.clearTimeout(waiter.timer);
          this.resumeWaiters.delete(message.sha256);
          waiter.resolve({ offset: message.offset, alreadyPresent: message.alreadyPresent === true });
        }
      } else if (message.type === "meta") {
        if (message.size < 0 || message.size > MAX_TRANSFER_BYTES) throw new Error("Incoming artifact exceeds the transfer limit.");
        this.receiveMeta = message;
        this.receiveAlreadyPresent = await this.store.hasSha256(message.sha256);
        const pending = this.receiveAlreadyPresent ? undefined : await this.store.getPendingTransfer(message.sha256);
        this.receiveChunks = pending ? await Promise.all(pending.chunks.map((chunk) => chunk.arrayBuffer())) : [];
        this.receiveBytes = pending?.receivedBytes ?? (this.receiveAlreadyPresent ? message.size : 0);
        this.channel?.send(JSON.stringify({
          type: "resume",
          sha256: message.sha256,
          offset: this.receiveBytes,
          ...(this.receiveAlreadyPresent ? { alreadyPresent: true } : {}),
        } satisfies ResumeMessage));
        this.onStatus(`Receiving encrypted artifact ${message.name}…`);
      } else if (message.type === "complete") {
        await this.finishReceive();
      }
      return;
    }

    if (!(data instanceof ArrayBuffer) || !this.receiveMeta) throw new Error("Unexpected peer payload.");
    if (this.receiveAlreadyPresent) throw new Error("Receiver already has this artifact.");
    this.receiveBytes += data.byteLength;
    if (this.receiveBytes > this.receiveMeta.size) throw new Error("Incoming artifact exceeded declared size.");
    const persistedBytes = await this.store.appendTransferChunk(this.receiveMeta, data);
    if (persistedBytes !== this.receiveBytes) throw new Error("Persisted transfer offset did not match received bytes.");
    this.receiveChunks.push(data);
    this.onProgress(this.receiveBytes, this.receiveMeta.size);
  }

  private async finishReceive(): Promise<void> {
    if (!this.receiveMeta || this.receiveBytes !== this.receiveMeta.size) throw new Error("Incoming artifact size did not match its manifest.");
    const metadata = this.receiveMeta;
    if (this.receiveAlreadyPresent) {
      this.onStatus(`Received artifact already present: ${metadata.name}.`);
      this.receiveChunks = [];
      this.receiveBytes = 0;
      this.receiveMeta = undefined;
      this.receiveAlreadyPresent = false;
      this.onProgress(0, 0);
      return;
    }
    const bytes = new Uint8Array(this.receiveBytes);
    let offset = 0;
    for (const chunk of this.receiveChunks) {
      bytes.set(new Uint8Array(chunk), offset);
      offset += chunk.byteLength;
    }
    const fileBuffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(fileBuffer).set(bytes);
    const file = new File([fileBuffer], metadata.name, { type: "application/octet-stream" });
    const result = await this.store.importFile(file);
    this.onStatus(result.duplicate
      ? `Received artifact already present: ${file.name}.`
      : result.nameConflict
        ? `Received ${file.name}; same name exists with different bytes.`
        : `Received artifact ${file.name}.`);
    this.receiveChunks = [];
    this.receiveBytes = 0;
    this.onProgress(0, 0);
    this.receiveMeta = undefined;
    this.receiveAlreadyPresent = false;
    await this.store.clearPendingTransfer(metadata.sha256);
  }

  private reset(): void {
    this.channel?.close();
    this.connection?.close();
    this.channel = undefined;
    this.connection = undefined;
    this.role = "idle";
    this.receiveChunks = [];
    this.receiveBytes = 0;
    this.receiveMeta = undefined;
    this.receiveAlreadyPresent = false;
    this.receiveBundle = undefined;
    this.receiveBundleIndex = undefined;
    for (const waiter of this.fileAckWaiters.values()) {
      window.clearTimeout(waiter.timer);
      waiter.reject(new Error("Peer transfer session reset."));
    }
    this.fileAckWaiters.clear();
    for (const waiter of this.resumeWaiters.values()) {
      window.clearTimeout(waiter.timer);
      waiter.reject(new Error("Peer transfer session reset."));
    }
    this.resumeWaiters.clear();
    this.onProgress(0, 0);
  }

  private waitForFileAck(index: number, timeoutMs = 30_000): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.fileAckWaiters.delete(index);
        reject(new Error(`Receiver did not acknowledge bundle file ${index}.`));
      }, timeoutMs);
      this.fileAckWaiters.set(index, { resolve, reject, timer });
    });
  }

  private waitForResume(sha256: string, timeoutMs = 30_000): Promise<ResumeState> {
    return new Promise<ResumeState>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.resumeWaiters.delete(sha256);
        reject(new Error("Receiver did not return a resumable transfer offset."));
      }, timeoutMs);
      this.resumeWaiters.set(sha256, { resolve, reject, timer });
    });
  }
}

function encodeSignal(signal: Signal): string {
  const bytes = new TextEncoder().encode(JSON.stringify(signal));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function decodeSignal(code: string): Signal {
  const normalized = code.trim().replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const signal = JSON.parse(new TextDecoder().decode(bytes)) as Signal;
  if (signal.v !== SIGNAL_VERSION || !signal.sdp?.type || !signal.sdp.sdp) throw new Error("Signal code is invalid.");
  return signal;
}

async function waitForIceGathering(connection: RTCPeerConnection, timeoutMs = 20_000): Promise<void> {
  if (connection.iceGatheringState === "complete") return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("ICE gathering timed out.")), timeoutMs);
    const handler = () => {
      if (connection.iceGatheringState === "complete") {
        window.clearTimeout(timer);
        connection.removeEventListener("icegatheringstatechange", handler);
        resolve();
      }
    };
    connection.addEventListener("icegatheringstatechange", handler);
  });
}

async function waitForDataChannelOpen(channel: RTCDataChannel, timeoutMs = 20_000): Promise<void> {
  if (channel.readyState === "open") return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("Peer DataChannel did not open.")), timeoutMs);
    channel.addEventListener("open", () => {
      window.clearTimeout(timer);
      resolve();
    }, { once: true });
    channel.addEventListener("error", () => reject(new Error("Peer DataChannel failed.")), { once: true });
  });
}

async function waitForBufferedAmount(channel: RTCDataChannel): Promise<void> {
  if (channel.bufferedAmount < 2 * CHUNK_BYTES) return;
  await new Promise<void>((resolve) => {
    channel.bufferedAmountLowThreshold = CHUNK_BYTES;
    channel.addEventListener("bufferedamountlow", () => resolve(), { once: true });
  });
}
